import { useState } from 'react';
import { Scissors, ChevronRight, ChevronLeft, Check, User } from 'lucide-react';
import { clsx } from 'clsx';
import { addLocalDays, formatLocalDateInput, formatLocalDateDisplay, buildLocalDateTime } from '../../utilities/dateUtils';
import { getBusinessHoursForDate } from '../../utilities/appointmentSlots';
import { Button, Input } from '../ui';

// ─── Dados estáticos ─────────────────────────────────────────────────────────

const SERVICES = [
  { value: 'Cabelo', label: 'Cabelo', duration: '50 min', description: 'Corte completo' },
  { value: 'Barba', label: 'Barba', duration: '50 min', description: 'Barba e acabamento' },
  { value: 'Cabelo e Barba', label: 'Cabelo e Barba', duration: '100 min', description: 'Combo completo' },
];

const BARBERS = [
  { value: 'Carlos', initials: 'CA' },
  { value: 'Donizete', initials: 'DO' },
];

const STEPS = ['Serviço', 'Data', 'Profissional e Horário', 'Confirmação'];

// Dias disponiveis para agendamento: 7 dias contando o atual, pulando domingos.
function getSelectableDays() {
  const days = [];
  let offset = 0;
  while (days.length < 7) {
    const day = addLocalDays(new Date(), offset);
    offset += 1;
    if (!day || day.getDay() === 0) continue; // pula domingo
    days.push(day);
  }
  return days;
}

// Hoje so e clicavel se ainda houver horario futuro (mesmo buffer de 10 min da Home).
function isDayBookable(value) {
  if (value !== formatLocalDateInput(new Date())) return true; // dias futuros sempre ok
  const hours = getBusinessHoursForDate(value);
  const lastTime = buildLocalDateTime(value, hours[hours.length - 1])?.getTime();
  return !!lastTime && Date.now() <= lastTime - 600000;
}

// ─── Stepper indicator ───────────────────────────────────────────────────────

function StepIndicator({ current }) {
  return (
    <div className="flex items-center justify-center gap-0" aria-label="Progresso do agendamento">
      {STEPS.map((label, i) => {
        const idx = i + 1;
        const done = idx < current;
        const active = idx === current;
        return (
          <div key={label} className="flex items-center">
            <div className="flex flex-col items-center gap-1">
              <div
                aria-current={active ? 'step' : undefined}
                className={clsx(
                  'w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors duration-normal',
                  done && 'bg-primary text-primary-foreground',
                  active && 'bg-primary text-primary-foreground ring-4 ring-primary-subtle',
                  !done && !active && 'bg-bg-secondary text-text-muted border border-border',
                )}
              >
                {done ? <Check size={14} /> : idx}
              </div>
              <span className={clsx(
                'text-xs hidden sm:block',
                active ? 'text-text-primary font-medium' : 'text-text-muted',
              )}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={clsx(
                // self-start + mt-4 alinha a linha ao centro vertical do circulo (h-8 => 16px)
                'w-10 sm:w-16 h-px mx-1 self-start mt-4 transition-colors duration-normal',
                idx < current ? 'bg-primary' : 'bg-border',
              )} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Step 1: Serviço ─────────────────────────────────────────────────────────

function StepService({ service, onSelect }) {
  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold text-text-primary">Qual serviço?</h2>
      <div className="flex flex-col gap-2">
        {SERVICES.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => onSelect(s.value)}
            className={clsx(
              'flex items-center gap-4 w-full text-left px-4 py-4 rounded-lg border transition-all duration-normal',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
              service === s.value
                ? 'border-primary bg-primary-subtle text-primary'
                : 'border-border bg-surface hover:border-primary-200 hover:bg-bg-secondary text-text-primary',
            )}
          >
            <span className={clsx(
              'w-10 h-10 rounded-full flex items-center justify-center shrink-0',
              service === s.value ? 'bg-primary text-primary-foreground' : 'bg-bg-secondary text-text-muted',
            )}>
              <Scissors size={18} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold">{s.label}</span>
              <span className="block text-xs text-text-muted mt-0.5">{s.description} · {s.duration}</span>
            </span>
            {service === s.value && <Check size={16} className="shrink-0 text-primary" />}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Step 2: Data ────────────────────────────────────────────────────────────

function StepDate({ date, handleDateChange }) {
  const days = getSelectableDays();

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold text-text-primary">Escolha a data</h2>
      <div className="grid grid-cols-3 gap-2" role="group" aria-label="Dias disponíveis">
        {days.map((d) => {
          const value = formatLocalDateInput(d);
          const selected = date === value;
          const bookable = isDayBookable(value);
          const weekday = d.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
          return (
            <button
              key={value}
              type="button"
              disabled={!bookable}
              onClick={() => bookable && handleDateChange({ target: { value } })}
              className={clsx(
                'flex flex-col items-center justify-center py-3 rounded-md border transition-all duration-normal',
                'last:col-start-2', // 7º dia fica sozinho na ultima linha: centraliza na coluna do meio
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
                selected && 'bg-primary text-primary-foreground border-primary',
                !selected && bookable && 'bg-surface border-border text-text-primary hover:border-primary-200 hover:bg-bg-secondary',
                !bookable && 'bg-bg-secondary border-border text-text-muted opacity-50 cursor-not-allowed',
              )}
            >
              <span className="text-xs capitalize opacity-80">{weekday}</span>
              <span className="text-lg font-semibold leading-tight">{d.getDate()}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step 3: Profissional e Horário ──────────────────────────────────────────

function StepProfessionalHour({
  professional, noDayOffBarber, onSelect,
  hour, exclusiveHours, handleHourChange,
}) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold text-text-primary">Escolha o barbeiro</h2>
        <div className="grid grid-cols-2 gap-3">
          {BARBERS.map((b) => {
            const available = (noDayOffBarber || []).includes(b.value);
            const selected = professional === b.value;
            return (
              <button
                key={b.value}
                type="button"
                disabled={!available}
                onClick={() => available && onSelect(b.value)}
                className={clsx(
                  'flex flex-col items-center gap-3 py-6 px-4 rounded-lg border transition-all duration-normal',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
                  selected && 'border-primary bg-primary-subtle',
                  !selected && available && 'border-border bg-surface hover:border-primary-200 hover:bg-bg-secondary',
                  !available && 'border-border bg-bg-secondary opacity-50 cursor-not-allowed',
                )}
              >
                <span className={clsx(
                  'w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold',
                  selected ? 'bg-primary text-primary-foreground' : 'bg-bg-secondary text-text-muted',
                )}>
                  {available ? b.initials : <User size={20} />}
                </span>
                <span className="flex flex-col items-center gap-0.5">
                  <span className={clsx('text-sm font-semibold', selected ? 'text-primary' : 'text-text-primary')}>
                    {b.value}
                  </span>
                  <span className="text-xs text-text-muted">
                    {available ? 'Disponível' : 'De folga'}
                  </span>
                </span>
                {selected && <Check size={14} className="text-primary" />}
              </button>
            );
          })}
        </div>
      </div>

      {professional && (
        <div>
          <h2 className="text-lg font-semibold text-text-primary mb-3">Horários disponíveis</h2>
          {exclusiveHours.length === 0 ? (
            <p className="text-sm text-text-muted py-2">Nenhum horário disponível para esta data.</p>
          ) : (
            <div className="grid grid-cols-3 gap-2" role="group" aria-label="Horários disponíveis">
              {exclusiveHours.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => handleHourChange({ target: { value: h } })}
                  className={clsx(
                    'py-2.5 text-sm font-medium rounded-md border transition-all duration-normal',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
                    hour[0] === h
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-surface border-border text-text-primary hover:border-primary-200 hover:bg-bg-secondary',
                  )}
                >
                  {h}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Step 4: Confirmação ─────────────────────────────────────────────────────

function StepReview({
  name, cel, service, professional, date, hour,
  setName, handleCelChange, loading, formSubmitted, user, isAdmin,
}) {
  return (
    <div className="flex flex-col gap-5">
      <h2 className="text-lg font-semibold text-text-primary">Confirme seus dados</h2>

      {/* Resumo */}
      <div className="bg-bg-secondary rounded-lg border border-border divide-y divide-border">
        {[
          { label: 'Serviço', value: service },
          { label: 'Barbeiro', value: professional },
          { label: 'Data', value: formatLocalDateDisplay(date) },
          { label: 'Horário', value: hour[0] },
        ].map(({ label, value }) => (
          <div key={label} className="flex items-center justify-between px-4 py-3">
            <span className="text-sm text-text-muted">{label}</span>
            <span className="text-sm font-medium text-text-primary">{value}</span>
          </div>
        ))}
      </div>

      {/* Dados pessoais */}
      <div className="flex flex-col gap-3">
        <Input
          label="Nome completo"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Seu nome completo"
          required
          disabled={!!user && !isAdmin}
        />
        <Input
          label="Celular"
          value={cel}
          onChange={handleCelChange}
          placeholder="(19) 99999-9999"
          required
          disabled={!!user && !isAdmin}
        />
      </div>
    </div>
  );
}

// ─── Main ScheduleForm (stepper) ─────────────────────────────────────────────

export default function ScheduleForm({
  name, cel, date, hour, service, professional,
  setName, setCel,
  handleSubmit, loading, formSubmitted,
  user, isAdmin,
  noDayOffBarber, exclusiveHours,
  handleDateChange, handleHourChange, handleCelChange,
  handleProfessionalChange, handelServiceChange,
}) {
  const [step, setStep] = useState(1);

  const canAdvance = () => {
    if (step === 1) return !!service;
    if (step === 2) return !!date;
    if (step === 3) return !!professional && hour.length > 0;
    return true;
  };

  const handleServiceSelect = (value) => {
    handelServiceChange({ target: { value } });
  };

  const handleProfessionalSelect = (value) => {
    handleProfessionalChange({ target: { value } });
  };

  const advance = () => { if (canAdvance()) setStep((s) => s + 1); };
  const back = () => setStep((s) => s - 1);

  // So permite gravar a partir da etapa de confirmacao (4). Garante que o
  // agendamento nunca seja enviado sozinho antes do clique em "Confirmar".
  const onFormSubmit = (e) => {
    e.preventDefault();
    if (step !== 4) return;
    handleSubmit(e);
  };

  return (
    <form onSubmit={onFormSubmit} noValidate>
      <div className="flex flex-col gap-6">
        <StepIndicator current={step} />

        <div className="min-h-[260px]">
          {step === 1 && (
            <StepService service={service} onSelect={handleServiceSelect} />
          )}
          {step === 2 && (
            <StepDate
              date={date}
              handleDateChange={handleDateChange}
            />
          )}
          {step === 3 && (
            <StepProfessionalHour
              professional={professional}
              noDayOffBarber={noDayOffBarber}
              onSelect={handleProfessionalSelect}
              hour={hour}
              exclusiveHours={exclusiveHours}
              handleHourChange={handleHourChange}
            />
          )}
          {step === 4 && (
            <StepReview
              name={name} cel={cel} service={service}
              professional={professional} date={date} hour={hour}
              setName={setName} handleCelChange={handleCelChange}
              loading={loading} formSubmitted={formSubmitted}
              user={user} isAdmin={isAdmin}
            />
          )}
        </div>

        {/* Navegação */}
        <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
          {step > 1 ? (
            <Button type="button" variant="secondary" size="md" onClick={back}>
              <ChevronLeft size={16} /> Voltar
            </Button>
          ) : (
            <div />
          )}

          {step < 4 ? (
            // key distinto do botao de submit: evita que o React reaproveite o
            // mesmo no e troque seu type para "submit" durante o clique, o que
            // dispararia o envio do formulario sozinho ao chegar na ultima etapa.
            <Button
              key="advance"
              type="button"
              variant="primary"
              size="md"
              disabled={!canAdvance()}
              onClick={advance}
            >
              Continuar <ChevronRight size={16} />
            </Button>
          ) : (
            <Button
              key="confirm"
              type="submit"
              variant="primary"
              size="md"
              loading={loading || formSubmitted}
              disabled={!name || !cel}
            >
              {loading || formSubmitted ? 'Aguarde...' : 'Confirmar agendamento'}
            </Button>
          )}
        </div>
      </div>
    </form>
  );
}
